import React, { createContext, useContext, useEffect, useState } from "react";
import { parseBqnsAndHostnames, parseClusterStatusResponse } from "./api";

const BqnContext = createContext({
  bqnId: undefined,
  loginBqnId: undefined,
  clusterStatus: null,
  setBqnId: () => {},
  bqns: undefined,
  onlineBqns: [],
  loadClusterStatus: () => {},
  loadOnce: () => {},
});

window.bqnIdReady = new Promise((resolve, reject) => {
  window.bqnIdReadyResolve = resolve;
});

export function useBqnId() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.bqnId;
}

export function useBqns() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.bqns;
}

export function usePrimaryBqn() {
  const { bqns } = useContext(BqnContext);

  if (bqns === undefined) {
    return undefined;
  }

  const primaryBqn = bqns.find(
    ({ function: functionName }) => functionName === "primary",
  );
  if (primaryBqn && primaryBqn.bqnId) {
    return primaryBqn.bqnId;
  }
  return undefined;
}

export function useClusterStatus() {
  const bqnContext = useContext(BqnContext);
  return bqnContext.clusterStatus;
}

export const BqnContextProvider = ({ children }) => {
  const [bqnId, setBqnId] = useState();
  const [clusterStatus, setClusterStatus] = useState();
  const [bqns, setBqns] = useState();
  const [onlineBqns, setOnlineBqns] = useState([]);
  const [loginBqnId, setLoginBqnId] = useState();

  useEffect(() => {
    if (bqnId === undefined || loginBqnId === undefined) {
      return;
    }

    window.bqnId = bqnId; // For usage from the JS only. Use context from React.
    window.bqnIdReadyResolve();
    window.globalSetBqnId = innerSetBqnId;
  }, [bqnId, loginBqnId]);

  useEffect(() => {
    if (bqns) {
      setOnlineBqns(
        bqns.filter(({ function: _function }) => _function !== "offline"),
      );

      if (bqns.length > 1) {
        ifCl.run("system users ssh sync");
      }
    }
  }, [JSON.stringify(bqns)]);

  function innerSetBqnId(bqnId) {
    setBqnId(bqnId);
  }

  const loadClusterStatus = () =>
    ifCl
      .run(
        "show cluster bqn\nshow system hostname detail\nshow cluster status\nshow system bqn id\n",
        true,
      )
      .then((response) => {
        const [response1, response2, response3, response4] =
          response.split(",");
        const clusterBqnRes = parseBqnsAndHostnames(response1, response2);
        const clusterStatusRes = parseClusterStatusResponse(response3);
        setBqns(clusterBqnRes);
        setLoginBqnId(Number(response4.trim().trim("\n")));

        if (clusterStatusRes) {
          setClusterStatus(clusterStatusRes);
        }
        const bqnId =
          clusterStatusRes?.mode === "standalone"
            ? Number(clusterStatusRes.bqnId)
            : window.bqnId === undefined
            ? "all"
            : window.bqnId;

        setBqnId(bqnId);

        return { bqnId, bqns: clusterBqnRes };
      });

  function loadClusterBqn() {
    return ifCl
      .run("show cluster bqn\nshow system hostname detail\n", true)
      .then((response) => {
        const [response1, response2] = response.trim().split(",");
        setBqns(parseBqnsAndHostnames(response1, response2));
      })
      .catch(console.error);
  }

  function loadOnce() {
    if (bqnId === undefined && bqns === undefined) {
      loadClusterStatus();
    }
  }

  return (
    <BqnContext.Provider
      value={{
        setBqnId: innerSetBqnId,
        bqnId,
        clusterStatus,
        loadOnce,
        bqns,
        loadClusterStatus,
        loadClusterBqn,
        loginBqnId,
        onlineBqns,
      }}
    >
      {children}
    </BqnContext.Provider>
  );
};

export default BqnContext;
